/* eslint-disable react-hooks/exhaustive-deps */
/*
 * Title: Reconcile Raw Data - Index
 * Description: Main layout with summary, filter, and item list
 */

import { useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Row, Spinner, Table } from 'react-bootstrap';
import { useReconcileRawData } from './ContextAPI';
import ItemCard from './ItemCard';

export default function Index() {
    const {
        results,
        loading,
        showResults,
        filterType,
        setFilterType,
        filteredItems,
        runReconcile,
        clearResults,
    } = useReconcileRawData();

    const [confirmExecute, setConfirmExecute] = useState(false);

    const summary = results?.summary;

    const typeButtons = useMemo(
        () => [
            { key: 'all', label: 'All', color: 'secondary', count: summary?.total_excel_items },
            { key: 'gain', label: 'Gain', color: 'success', count: summary?.gain_count },
            { key: 'loss', label: 'Loss', color: 'danger', count: summary?.loss_count },
            { key: 'equal', label: 'Equal', color: 'info', count: summary?.equal_count },
            { key: 'not_found_in_db', label: 'Not Found', color: 'warning', count: summary?.not_found_count },
            { key: 'db_only', label: 'DB Only', color: 'dark', count: summary?.db_only_count },
        ],
        [summary]
    );

    const handleExecute = () => {
        if (!confirmExecute) {
            setConfirmExecute(true);
            setTimeout(() => setConfirmExecute(false), 5000);
            return;
        }
        setConfirmExecute(false);
        runReconcile('execute');
    };

    return (
        <Card>
            <Card.Header className="cardHeader d-flex justify-content-between align-items-center">
                <h3 className="mb-0">Reconcile Raw Data</h3>
                <div className="d-flex gap-2">
                    {showResults && (
                        <Button variant="outline-secondary" onClick={clearResults}>
                            Clear Results
                        </Button>
                    )}
                    <Button
                        className="btnInfo"
                        variant="primary"
                        onClick={() => runReconcile('simulate')}
                        disabled={loading}
                    >
                        {loading ? <Spinner animation="border" size="sm" /> : 'Preview (Simulate)'}
                    </Button>
                    <Button
                        variant={confirmExecute ? 'danger' : 'success'}
                        onClick={handleExecute}
                        disabled={loading}
                    >
                        {loading ? (
                            <Spinner animation="border" size="sm" />
                        ) : confirmExecute ? (
                            'Click Again to Confirm'
                        ) : (
                            'Execute Reconcile'
                        )}
                    </Button>
                </div>
            </Card.Header>
            <Card.Body>
                {!showResults && !loading && (
                    <div className="text-center py-5">
                        <h5 className="text-muted">Click &quot;Preview (Simulate)&quot; to see what changes will be made</h5>
                        <p className="text-muted">
                            The system reads Final Reconciliation File.json and compares with database records.
                        </p>
                    </div>
                )}

                {loading && (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <h5 className="mt-3 text-muted">
                            {results ? 'Executing reconciliation...' : 'Processing reconciliation data...'}
                        </h5>
                        <p className="text-muted">This may take a moment for large datasets.</p>
                    </div>
                )}

                {showResults && summary && (
                    <>
                        {/* Mode Badge */}
                        <div className="mb-3">
                            <Badge bg={results.mode === 'execute' ? 'success' : 'primary'} className="fs-6">
                                {results.mode === 'execute' ? 'EXECUTED' : 'PREVIEW ONLY (Simulate)'}
                            </Badge>
                        </div>

                        {/* Summary Cards */}
                        <Row className="mb-4">
                            <Col lg={3}>
                                <Card className="text-center border">
                                    <Card.Body className="py-2">
                                        <small className="text-muted d-block">Excel Items</small>
                                        <strong className="fs-4">{summary.total_excel_items}</strong>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col lg={3}>
                                <Card className="text-center border">
                                    <Card.Body className="py-2">
                                        <small className="text-muted d-block">DB Rows Before</small>
                                        <strong className="fs-4">{summary.total_db_detail_rows_before?.toLocaleString()}</strong>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col lg={3}>
                                <Card className="text-center border">
                                    <Card.Body className="py-2">
                                        <small className="text-muted d-block">DB Rows After</small>
                                        <strong className="fs-4">{summary.total_db_detail_rows_after?.toLocaleString()}</strong>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col lg={3}>
                                <Card className="text-center border">
                                    <Card.Body className="py-2">
                                        <small className="text-muted d-block">Total Available Qty Change</small>
                                        <strong className="fs-4">
                                            {summary.total_available_qty_before?.toLocaleString()}{' '}
                                            <i className="fas fa-arrow-right mx-1" />{' '}
                                            {summary.total_available_qty_after?.toLocaleString()}
                                        </strong>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Detailed Summary Table */}
                        <Table bordered size="sm" className="mb-4">
                            <thead className="table-light">
                                <tr>
                                    <th>Gain Items</th>
                                    <th>Loss Items</th>
                                    <th>Equal Items</th>
                                    <th>Not Found in DB</th>
                                    <th>DB Only Items</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>
                                        <Badge bg="success">{summary.gain_count}</Badge>
                                    </td>
                                    <td>
                                        <Badge bg="danger">{summary.loss_count}</Badge>
                                    </td>
                                    <td>
                                        <Badge bg="info">{summary.equal_count}</Badge>
                                    </td>
                                    <td>
                                        <Badge bg="warning">{summary.not_found_count}</Badge>
                                    </td>
                                    <td>
                                        <Badge bg="dark">{summary.db_only_count}</Badge>
                                    </td>
                                </tr>
                            </tbody>
                        </Table>

                        {/* Filter Buttons */}
                        <div className="mb-3 d-flex gap-2 flex-wrap">
                            {typeButtons.map((btn) => (
                                <Button
                                    key={btn.key}
                                    variant={filterType === btn.key ? btn.color : `outline-${btn.color}`}
                                    size="sm"
                                    onClick={() => setFilterType(btn.key)}
                                >
                                    {btn.label} ({btn.count || 0})
                                </Button>
                            ))}
                        </div>

                        {/* Items List */}
                        <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                            {filteredItems.length === 0 && (
                                <p className="text-center text-muted py-3">No items match this filter.</p>
                            )}
                            {filteredItems.map((item) => (
                                <ItemCard key={`${item.pms_code}-${item.po_number}-${item.batch_number}-${item.sl_no}`} item={item} />
                            ))}
                        </div>
                    </>
                )}
            </Card.Body>
        </Card>
    );
}
